import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './RadialMenu.module.css';
import { IRadialMenuItem, IRadialMenuProps } from './RadialMenu.types';

type AnimState = 'entering' | 'visible' | 'exiting';

const RadialMenu: React.FC<IRadialMenuProps> = (props) => {
    const {
        items,
        isOpen,
        onDismiss,
        radius = 170,
        itemSize = 96,
        centerSize = 72,
        centerLabel,
        maxPerRing = 8,
        disableAnimations = false,
    } = props;

    const [menuStack, setMenuStack] = useState<IRadialMenuItem[][]>([items]);
    const [labelStack, setLabelStack] = useState<string[]>([]);
    const [animState, setAnimState] = useState<AnimState>('visible');
    const [discoveredItems, setDiscoveredItems] = useState<Record<string, IRadialMenuItem[]>>({});
    const [loadingItemKey, setLoadingItemKey] = useState<string | null>(null);
    const animTimeout = useRef<number | null>(null);

    // Reset stack when items change or menu opens
    useEffect(() => {
        if (isOpen) {
            setMenuStack([items]);
            setLabelStack([]);
            setAnimState('visible');
            setDiscoveredItems({});
            setLoadingItemKey(null);
        }
    }, [isOpen, items]);

    const currentItems = menuStack[menuStack.length - 1] ?? [];

    const pushSubMenu = useCallback((label: string, childItems: IRadialMenuItem[]) => {
        if (disableAnimations) {
            setMenuStack((prev) => [...prev, childItems]);
            setLabelStack((prev) => [...prev, label]);
            setAnimState('visible');
            return;
        }

        setAnimState('exiting');
        if (animTimeout.current) clearTimeout(animTimeout.current);
        animTimeout.current = window.setTimeout(() => {
            setMenuStack((prev) => [...prev, childItems]);
            setLabelStack((prev) => [...prev, label]);
            setAnimState('entering');
            animTimeout.current = window.setTimeout(() => {
                setAnimState('visible');
            }, 50);
        }, 200);
    }, [disableAnimations]);

    const navigateTo = useCallback(async (item: IRadialMenuItem) => {
        const knownItems = item.items ?? discoveredItems[item.key];
        if (knownItems && knownItems.length > 0) {
            pushSubMenu(item.label, knownItems);
            return;
        }

        if (item.getItems) {
            try {
                setLoadingItemKey(item.key);
                const loadedItems = await Promise.resolve(item.getItems());
                const normalizedItems = loadedItems ?? [];

                setDiscoveredItems((prev) => ({
                    ...prev,
                    [item.key]: normalizedItems,
                }));

                if (normalizedItems.length > 0) {
                    pushSubMenu(item.label, normalizedItems);
                    return;
                }
            } finally {
                setLoadingItemKey((prev) => (prev === item.key ? null : prev));
            }
        }

        if (item.onClick) {
            item.onClick();
            onDismiss?.();
        }
    }, [discoveredItems, onDismiss, pushSubMenu]);

    const navigateBack = useCallback(() => {
        if (menuStack.length <= 1) {
            onDismiss?.();
            return;
        }

        if (disableAnimations) {
            setMenuStack((prev) => prev.slice(0, -1));
            setLabelStack((prev) => prev.slice(0, -1));
            setAnimState('visible');
            return;
        }

        setAnimState('exiting');
        if (animTimeout.current) clearTimeout(animTimeout.current);
        animTimeout.current = window.setTimeout(() => {
            setMenuStack((prev) => prev.slice(0, -1));
            setLabelStack((prev) => prev.slice(0, -1));
            setAnimState('entering');
            animTimeout.current = window.setTimeout(() => {
                setAnimState('visible');
            }, 50);
        }, 200);
    }, [disableAnimations, menuStack.length, onDismiss]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (animTimeout.current) clearTimeout(animTimeout.current);
        };
    }, []);

    // Hidden search: capture keystrokes with debounce
    const [searchQuery, setSearchQuery] = useState('');
    const searchTimeout = useRef<number | null>(null);

    useEffect(() => {
        if (!isOpen) {
            setSearchQuery('');
            return;
        }
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape' || e.key === 'Enter' || e.ctrlKey || e.altKey || e.metaKey) return;
            if (e.key === 'Backspace') {
                setSearchQuery((prev) => prev.slice(0, -1));
            } else if (e.key.length === 1) {
                setSearchQuery((prev) => prev + e.key);
            }
            // Auto-clear after 2s of no typing
            if (searchTimeout.current) clearTimeout(searchTimeout.current);
            searchTimeout.current = window.setTimeout(() => {
                setSearchQuery('');
            }, 2000);
        };
        document.addEventListener('keydown', handler);
        return () => {
            document.removeEventListener('keydown', handler);
            if (searchTimeout.current) clearTimeout(searchTimeout.current);
        };
    }, [isOpen]);

    const searchLower = searchQuery.toLowerCase();

    // Close on Escape
    useEffect(() => {
        if (!isOpen) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                if (searchQuery) {
                    setSearchQuery('');
                } else if (menuStack.length > 1) {
                    navigateBack();
                } else {
                    onDismiss?.();
                }
            }
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [isOpen, menuStack.length, navigateBack, onDismiss, searchQuery]);

    // Distribute items across rings where each ring's capacity scales with its circumference
    const rings = useMemo(() => {
        const count = currentItems.length;
        if (count === 0) return [];
        const ringGap = itemSize + 20;
        const result: { startIdx: number; count: number; radius: number }[] = [];
        let placed = 0;
        let ringIdx = 0;
        while (placed < count) {
            const ringRadius = radius + ringIdx * ringGap;
            // Capacity based on circumference: how many items fit with spacing
            const circumference = 2 * Math.PI * ringRadius;
            const capacity = ringIdx === 0
                ? maxPerRing
                : Math.floor(circumference / (itemSize + 12));
            const countInRing = Math.min(capacity, count - placed);
            result.push({ startIdx: placed, count: countInRing, radius: ringRadius });
            placed += countInRing;
            ringIdx++;
        }
        return result;
    }, [currentItems.length, radius, maxPerRing, itemSize]);

    const itemPositions = useMemo(() => {
        const count = currentItems.length;
        if (count === 0) return [];
        const positions: { x: number; y: number }[] = new Array(count);
        rings.forEach((ring, ringIdx) => {
            const angleStep = (2 * Math.PI) / ring.count;
            const startAngle = -Math.PI / 2 + (ringIdx * angleStep) / 2;
            for (let i = 0; i < ring.count; i++) {
                const angle = startAngle + i * angleStep;
                positions[ring.startIdx + i] = {
                    x: Math.cos(angle) * ring.radius,
                    y: Math.sin(angle) * ring.radius,
                };
            }
        });
        return positions;
    }, [currentItems, rings]);

    // Determine max radius used to size SVG
    const outerRing = rings[rings.length - 1];
    const maxRadius = outerRing ? outerRing.radius : radius;
    const svgSize = (maxRadius + itemSize) * 2 + 20;
    const cx = svgSize / 2;
    const cy = svgSize / 2;
    const halfItem = itemSize / 2;
    const halfCenter = centerSize / 2;

    const centerText = menuStack.length > 1
        ? '\u2190'
        : (centerLabel || '\u2715');

    return (
        <div
            className={`${styles.overlay} ${isOpen ? styles.open : ''} ${disableAnimations ? styles.noAnimation : ''}`}
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) onDismiss?.();
            }}
        >
            {/* Breadcrumb */}
            {labelStack.length > 0 && (
                <div className={styles.breadcrumb}>
                    {labelStack.join(' \u203A ')}
                </div>
            )}

            {/* Search indicator */}
            {searchQuery && (
                <div className={styles.searchIndicator}>
                    Search: {searchQuery}
                </div>
            )}

            <svg
                className={styles.svgRoot}
                width={svgSize}
                height={svgSize}
                viewBox={`0 0 ${svgSize} ${svgSize}`}
                xmlns="http://www.w3.org/2000/svg"
            >
                {/* Item circles */}
                <g className={`${styles.ringGroup} ${styles[animState]}`}>
                    {currentItems.map((item, index) => {
                        const pos = itemPositions[index];
                        if (!pos) return null;
                        const ix = cx + pos.x;
                        const iy = cy + pos.y;
                        const hasChildren = Boolean(item.items?.length) || Boolean(item.getItems);
                        const isSearchMatch = searchLower
                            ? item.label.toLowerCase().includes(searchLower)
                            : false;
                        const isDimmed = searchLower && !isSearchMatch;
                        const strokeColor = isSearchMatch
                            ? '#4fc3f7'
                            : item.highlight
                                ? (item.color || '#f5d020')
                                : (item.color || 'rgba(200,200,220,0.4)');
                        const strokeW = isSearchMatch ? 3 : (item.highlight ? 3 : 2);
                        const groupOpacity = isDimmed ? 0.25 : 1;
                        return (
                            <g
                                key={item.key}
                                className={styles.itemNode}
                                onClick={() => {
                                    if (loadingItemKey === item.key) {
                                        return;
                                    }

                                    void navigateTo(item);
                                }}
                                style={{ cursor: 'pointer', opacity: groupOpacity, transition: 'opacity 0.2s ease' }}
                            >
                                {/* Highlight glow ring */}
                                {item.highlight && (
                                    <circle
                                        cx={ix}
                                        cy={iy}
                                        r={halfItem + 5}
                                        fill="none"
                                        stroke={item.color || '#f5d020'}
                                        strokeWidth={1.5}
                                        opacity={0.5}
                                    />
                                )}
                                <circle
                                    className={styles.itemCircle}
                                    cx={ix}
                                    cy={iy}
                                    r={halfItem}
                                    fill="rgba(18,18,30,0.94)"
                                    stroke={strokeColor}
                                    strokeWidth={strokeW}
                                />
                                {/* Hover hit area (invisible larger circle) */}
                                <circle
                                    cx={ix}
                                    cy={iy}
                                    r={halfItem + 4}
                                    fill="transparent"
                                    stroke="transparent"
                                >
                                    <title>{item.label}</title>
                                </circle>
                                {/* Icon or label text */}
                                {item.icon ? (
                                    <text
                                        x={ix}
                                        y={iy}
                                        textAnchor="middle"
                                        dominantBaseline="central"
                                        fontSize={20}
                                        fill="#fff"
                                        style={{ pointerEvents: 'none' }}
                                    >
                                        {item.icon}
                                    </text>
                                ) : (
                                    <foreignObject
                                        x={ix - halfItem + 6}
                                        y={iy - halfItem + 6}
                                        width={itemSize - 12}
                                        height={itemSize - 12}
                                        style={{ pointerEvents: 'none' }}
                                    >
                                        <div
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: '#eee',
                                                fontSize: 12,
                                                fontWeight: 500,
                                                textAlign: 'center',
                                                lineHeight: 1.25,
                                                wordBreak: 'break-word',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            {item.label}
                                        </div>
                                    </foreignObject>
                                )}
                                {/* Sub-menu indicator dot */}
                                {hasChildren && (
                                    <circle
                                        cx={ix}
                                        cy={iy}
                                        r={halfItem + 6}
                                        fill="none"
                                        stroke="rgba(200,200,220,0.35)"
                                        strokeWidth={1.5}
                                    />
                                )}

                                {loadingItemKey === item.key && (
                                    <text
                                        x={ix}
                                        y={iy + halfItem - 6}
                                        textAnchor="middle"
                                        fontSize={10}
                                            fill="#4fc3f7"
                                        style={{ pointerEvents: 'none' }}
                                    >
                                        Loading...
                                    </text>
                                )}

                            </g>
                        );
                    })}
                </g>

                {/* Center button */}
                <g
                    className={styles.centerNode}
                    onClick={navigateBack}
                    style={{ cursor: 'pointer' }}
                >
                    <circle
                        className={styles.centerCircle}
                        cx={cx}
                        cy={cy}
                        r={halfCenter}
                        fill="rgba(12,12,20,0.96)"
                        stroke="rgba(200,200,220,0.5)"
                        strokeWidth={2}
                    />
                    <text
                        x={cx}
                        y={cy}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={12}
                        fontWeight={600}
                        fill="#ddd"
                        style={{ pointerEvents: 'none', textTransform: 'uppercase', letterSpacing: '0.5px' } as React.CSSProperties}
                    >
                        {centerText}
                    </text>
                    <title>{menuStack.length > 1 ? 'Go back' : 'Close'}</title>
                </g>
            </svg>
        </div>
    );
};

export default RadialMenu;
